import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getSaldosRemitos } from "../helpers/rutaReportes";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10);

const sortES = (a, b) =>
  (a || "").localeCompare(b || "", "es", { sensitivity: "base" });

export default function ReporteSaldosRemitos() {
  const [fecha, setFecha] = useState(todayYYYYMMDD());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const consultar = async () => {
    setErr("");
    setLoading(true);
    try {
      const r = await getSaldosRemitos(fecha);
      if (!r?.ok) throw new Error(r?.error || r?.msg || "No se pudo cargar el reporte");
      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const tot = data?.totales || {};

  const clientes = useMemo(() => {
    return (data?.clientes || [])
      .filter((x) => Number(x?.saldo || 0) !== 0)
      .sort((a, b) => sortES(a?.razonsocial, b?.razonsocial));
  }, [data]);

  const proveedores = useMemo(() => {
    return (data?.proveedores || [])
      .filter((x) => Number(x?.saldo || 0) !== 0)
      .sort((a, b) => sortES(a?.razonsocial, b?.razonsocial));
  }, [data]);

  const exportarPDF = () => {
    if (!data) return;

    const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const title = "Reporte de Saldos (Remitos)";
    const subtitle = `Fecha de corte: ${data?.fechaCorte || fecha}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, 14, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subtitle, 14, 20);

    doc.setFontSize(10);
    doc.text(`Total Clientes: $ ${fmtARS(tot.clientes)}`, 14, 27);
    doc.text(`Total Proveedores: $ ${fmtARS(tot.proveedores)}`, 14, 32);
    doc.text(`Neto (Cli - Prov): $ ${fmtARS(tot.neto)}`, 14, 37);

    autoTable(doc, {
      startY: 43,
      head: [["Cliente", "Saldo", "Saldo A", "Saldo B"]],
      body: clientes.map((x) => [
        x.razonsocial || "",
        fmtARS(x.saldo),
        fmtARS(x.saldoA),
        fmtARS(x.saldoB),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
    });

    const y = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : 60;
    const needsNewPage = y > 260;
    if (needsNewPage) doc.addPage();

    const y2 = needsNewPage ? 14 : y;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Proveedores", 14, y2);

    autoTable(doc, {
      startY: y2 + 3,
      head: [["Proveedor", "Saldo", "Saldo A", "Saldo B", "Saldo Flete"]],
      body: proveedores.map((x) => [
        x.razonsocial || "",
        fmtARS(x.saldo),
        fmtARS(x.saldoA),
        fmtARS(x.saldoB),
        fmtARS(x.saldoFlete),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
    });

    doc.save(`reporte-saldos-remitos-${data?.fechaCorte || fecha}.pdf`);
  };

  return (
    <Container fluid="md" className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div style={{ minWidth: 240 }}>
              <Form.Group>
                <Form.Label className="mb-1">Fecha de corte</Form.Label>
                <Form.Control
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-2">
              <Button onClick={consultar} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" /> Consultando…
                  </>
                ) : (
                  "Consultar"
                )}
              </Button>

              <Button
                variant="secondary"
                onClick={exportarPDF}
                disabled={!data || loading}
              >
                Exportar PDF
              </Button>
            </div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {!data ? (
        <div className="mt-3 text-muted">
          Elegí una fecha y tocá <b>“Consultar”</b>.
        </div>
      ) : (
        <>
          {/* Totales (más “pegados” al nombre) */}
          <Row className="mt-3 g-2">
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total Clientes
                  </div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.clientes)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total Proveedores
                  </div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.proveedores)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Neto (Cli - Prov)
                  </div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.neto)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Clientes</h5>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th>Razón social</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo A</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo B</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientes.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          Sin saldos.
                        </td>
                      </tr>
                    ) : (
                      clientes.map((c) => (
                        <tr key={c.id || c._id || c.razonsocial}>
                          <td>{c.razonsocial}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldo)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldoA)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldoB)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Proveedores</h5>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th>Razón social</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo A</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo B</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo Flete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proveedores.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Sin saldos.
                        </td>
                      </tr>
                    ) : (
                      proveedores.map((p) => (
                        <tr key={p.id || p._id || p.razonsocial}>
                          <td>{p.razonsocial}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldo)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldoA)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldoB)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldoFlete)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}
